import React from "react";
import { Input } from "antd";

/**
 * Email-address entry: a chip list of the selected emails plus a free-text input
 * that accepts comma/space/Enter-separated addresses. Presentational — the
 * container owns `selectedEmails`/`emailSearchValue` and the parse/remove logic.
 *
 * @param {object} props
 * @param {{ email: string }[]} props.selectedEmails
 * @param {string} props.emailSearchValue
 * @param {(e: React.ChangeEvent<HTMLInputElement>) => void} props.onInputChange
 * @param {(e: React.KeyboardEvent) => void} props.onKeyDown
 * @param {() => void} props.onBlur
 * @param {(email: string) => void} props.onRemove
 */
export default function EmailChipInput({
  selectedEmails,
  emailSearchValue,
  onInputChange,
  onKeyDown,
  onBlur,
  onRemove,
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <label className="text-[#3f3f46] text-sm font-bold font-['Inter']">
          Email Addresses
        </label>
        <span className="text-xs text-gray-500 font-['Inter']">
          Separate multiple emails with comma, space, or Enter
        </span>
      </div>
      <div className="px-3 py-2 rounded-lg border border-gray-300 hover:border-blue-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/10 transition-all duration-200 flex items-center gap-2 min-h-[40px] flex-wrap bg-white">
        {/* Selected Email Tags */}
        {selectedEmails.map((email, index) => (
          <div
            key={index}
            className="px-2 py-1 bg-blue-50 rounded-md border border-blue-200 flex items-center gap-1.5 animate-fadeIn"
          >
            <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="text-blue-600">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none" />
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <span className="text-blue-700 text-xs font-medium font-['Inter']">
              {email.email}
            </span>
            <button
              className="w-3.5 h-3.5 flex items-center justify-center hover:bg-blue-200 rounded-full transition-colors duration-150"
              onClick={() => onRemove(email.email)}
            >
              <svg width="7" height="7" viewBox="0 0 8 8" fill="none" className="text-blue-500 hover:text-blue-700">
                <path d="M6 2L2 6M2 2L6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        ))}

        {/* Email Input */}
        <div className="flex-1 min-w-[200px] flex items-center">
          <Input
            value={emailSearchValue}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            placeholder={
              selectedEmails.length === 0
                ? "Enter email addresses (separate with comma, space, or Enter)..."
                : "Add more email addresses..."
            }
            className="w-full email-input"
            style={{ border: "none", boxShadow: "none", height: "32px" }}
            size="middle"
          />
        </div>
      </div>
    </div>
  );
}
